import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText } from 'lucide-react';
import AppShell from '@/components/AppShell';
import { TemplatePicker } from '@/components/lessonBuilder/TemplatePicker';
import { ContextForm } from '@/components/lessonBuilder/ContextForm';
import { GeneratedLessonView } from '@/components/lessonBuilder/GeneratedLessonView';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LessonPlanBuilder() {
  const navigate = useNavigate();
  const location = useLocation();
  const unitContext = location.state;

  const [projectTemplates, setProjectTemplates] = useState([]);
  const [essayTemplates, setEssayTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatedLesson, setGeneratedLesson] = useState(null);

  const [context, setContext] = useState({
    unit_title: unitContext?.unit_title || '',
    topic: unitContext?.topic || '',
    learning_objectives: '',
    grade_level: unitContext?.grade_level || 9,
    additional_notes: '',
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/lesson-templates`);
        setProjectTemplates(data.filter(t => t.type === 'project'));
        setEssayTemplates(data.filter(t => t.type === 'essay'));
      } catch (error) {
        toast.error('Failed to load templates');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleGenerate = async () => {
    if (!selectedTemplate) {
      toast.error('Please select a template');
      return;
    }
    if (!context.unit_title || !context.topic) {
      toast.error('Please provide unit title and topic');
      return;
    }
    try {
      setGenerating(true);
      const response = await axios.post(`${API}/lesson-templates/${selectedTemplate.id}/generate`, context);
      setGeneratedLesson(response.data.lesson_plan);
      toast.success('Lesson plan generated successfully!');
    } catch (error) {
      toast.error('Failed to generate lesson plan');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveToUnit = () => {
    if (unitContext?.onSave && generatedLesson) {
      unitContext.onSave(generatedLesson);
      toast.success('Lesson plan added to unit!');
      navigate(-1);
    } else {
      toast.info('Copy the lesson plan content to add it to your unit');
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-[#14213D] border-t-transparent"></div>
            <p className="mt-4 text-[#5A6577] text-sm">Loading templates…</p>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-10">
        <div className="mb-8 flex items-center gap-3">
          <FileText className="h-6 w-6 text-[#D4AF37]" />
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">AI Tools</p>
            <h1 className="font-serif-display text-3xl text-[#14213D] tracking-tight">Lesson Plan Builder</h1>
            <p className="text-sm text-[#5A6577] mt-1 max-w-2xl">
              Choose a template and generate complete lesson plans for projects or essays.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <TemplatePicker
              projectTemplates={projectTemplates}
              essayTemplates={essayTemplates}
              selectedTemplate={selectedTemplate}
              onSelect={setSelectedTemplate}
            />
            {selectedTemplate && (
              <ContextForm
                context={context}
                setContext={setContext}
                generating={generating}
                onGenerate={handleGenerate}
              />
            )}
          </div>
          <div>
            <GeneratedLessonView
              lesson={generatedLesson}
              context={context}
              canSaveToUnit={Boolean(unitContext?.onSave)}
              onSaveToUnit={handleSaveToUnit}
            />
          </div>
        </div>
      </div>
    </AppShell>
  );
}
